import React, { useState } from 'react';
import { Project } from '../../types/Project';
import { StatusBadge } from '../atoms/StatusBadge';
import { ProgressBar } from '../atoms/ProgressBar';
import { getTotalIssues, getIssuesList } from '../../utils/projectUtils';

interface ProjectCardProps {
  project: Project;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const totalIssues = getTotalIssues(project.issues);
  const issuesList = getIssuesList(project.issues);
  const hasIssues = totalIssues > 0;

  const formatDate = (date: string | null) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: '2-digit',
    });
  };

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1).replace('.', ',')}M`;
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}k`;
    }
    return `R$ ${value}`;
  };

  return (
    <div className={`bg-white/80 dark:bg-dark-300/80 backdrop-blur-sm rounded-3xl shadow-soft border-0 overflow-hidden ${
      project.status === 'done' ? 'opacity-50' : ''
    }`}>
      {/* Desktop View */}
      <div
        className={`hidden lg:grid p-6 grid-cols-[1fr_auto_80px_80px_100px_60px_60px_100px_140px_80px] gap-6 items-center ${
          hasIssues ? 'cursor-pointer hover:bg-cream-100/50 dark:hover:bg-dark-400/50' : ''
        }`}
        onClick={() => hasIssues && setIsExpanded(!isExpanded)}
      >
        {/* Title Column - Left aligned */}
        <div className="flex items-center gap-3 min-w-0">
          {hasIssues && (
            <svg
              className={`w-5 h-5 text-gray-400 dark:text-gray-500 transition-transform flex-shrink-0 ${
                isExpanded ? 'rotate-90' : ''
              }`}
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path d="M9 5l7 7-7 7" />
            </svg>
          )}
          <div className="min-w-0 flex-1">
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-gray-900 dark:text-gray-100 hover:text-warm-500 dark:hover:text-dark-purple-200 transition-colors block truncate"
              onClick={(e) => e.stopPropagation()}
              title={project.projectTitle}
            >
              {project.projectTitle}
            </a>
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
              {project.brandName}
            </p>
          </div>
        </div>

        {/* Status - Centered */}
        <div className="flex justify-center">
          <StatusBadge status={project.status} />
        </div>

        {/* Start Date - Centered */}
        <div className="text-xs text-gray-700 dark:text-gray-300 text-center">
          {formatDate(project.startDate)}
        </div>

        {/* End Date - Centered */}
        <div className="text-xs text-gray-700 dark:text-gray-300 text-center">
          {formatDate(project.endDate)}
        </div>

        {/* Budget - Centered */}
        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 text-center">
          {formatCurrency(project.totalBudget)}
        </div>

        {/* Terms - Centered */}
        <div className="flex justify-center">
          <div className={`w-3 h-3 rounded-full ${project.hasTerms ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
        </div>

        {/* Briefing - Centered */}
        <div className="flex justify-center">
          <div className={`w-3 h-3 rounded-full ${project.hasBriefing ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
        </div>

        {/* Creators - Centered */}
        <div className="text-sm text-gray-700 dark:text-gray-300 text-center">
          {project.approvedCreators}/{project.creatorsGoal}
        </div>

        {/* Progress - Centered with bar */}
        <div>
          <div className="flex items-center gap-2">
            <div className="flex-1">
              <ProgressBar percentage={project.completedPercent} showLabel={false} />
            </div>
            <div className="text-xs text-gray-600 dark:text-gray-400 font-semibold min-w-[32px] text-right">
              {project.completedPercent}%
            </div>
          </div>
        </div>

        {/* Issues - Centered with chip */}
        <div className="flex justify-center">
          {hasIssues ? (
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-full">
              <svg className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                {totalIssues}
              </span>
            </div>
          ) : (
            <span className="text-xs text-gray-400 dark:text-gray-600">-</span>
          )}
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden p-4 sm:p-5">
        <a
          href={project.url}
          target="_blank"
          rel="noopener noreferrer"
          className="block"
        >
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="min-w-0 flex-1">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1 truncate">
                {project.projectTitle}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {project.brandName}
              </p>
            </div>
            <div className="flex-shrink-0">
              <StatusBadge status={project.status} />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Início</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDate(project.startDate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Fim</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {formatDate(project.endDate)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Orçamento</p>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                {formatCurrency(project.totalBudget)}
              </p>
            </div>
            <div>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Creators</p>
              <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {project.approvedCreators}/{project.creatorsGoal}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-4 mb-4">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Termo:</span>
              <div className={`w-3 h-3 rounded-full ${project.hasTerms ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Briefing:</span>
              <div className={`w-3 h-3 rounded-full ${project.hasBriefing ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
            </div>
            {hasIssues && (
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500 dark:text-gray-400">Issues:</span>
                <div className="inline-flex items-center gap-1.5 px-2 py-0.5 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-full">
                  <svg className="w-3 h-3 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <span className="text-xs font-semibold text-amber-700 dark:text-amber-400">
                    {totalIssues}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 dark:text-gray-400">Progresso</span>
              <span className="text-xs text-gray-600 dark:text-gray-400 font-semibold">
                {project.completedPercent}%
              </span>
            </div>
            <ProgressBar percentage={project.completedPercent} showLabel={false} />
          </div>
        </a>

        {hasIssues && (
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-dark-400">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
            >
              <svg
                className={`w-4 h-4 transition-transform ${
                  isExpanded ? 'rotate-90' : ''
                }`}
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path d="M9 5l7 7-7 7" />
              </svg>
              {isExpanded ? 'Ocultar' : 'Ver'} pendências ({totalIssues})
            </button>
          </div>
        )}
      </div>

      {/* Issues List - Expanded */}
      {hasIssues && isExpanded && (
        <div className="px-6 pb-5 pt-4 border-t border-gray-100 dark:border-dark-400 bg-gray-50 dark:bg-dark-200/50">
          <div className="max-w-4xl">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
              <svg className="w-4 h-4 text-amber-600 dark:text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              Pendências Identificadas
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {issuesList.map((issue, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between p-3 bg-white dark:bg-dark-300 rounded-lg border border-gray-200 dark:border-dark-400"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{issue.label}</span>
                  </div>
                  <span className="inline-flex items-center justify-center min-w-[24px] h-6 px-2 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-md text-xs font-semibold">
                    {issue.count}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
